import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Search, MapPin, Image, Loader2, XCircle, ChevronLeft, ChevronRight } from "lucide-react";

const ITEMS_PER_PAGE = 10;

export default function Predictions() {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPlace, setFilterPlace] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalImage, setModalImage] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;
  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/predictions`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setPredictions(res.data);
      } catch (err) {
        setError("Tahminler alınamadı. Lütfen tekrar deneyin.");
      } finally {
        setLoading(false);
      }
    };
    fetchPredictions();
  }, []);

  const placeNameMap = {
    1: "Ayasofya Camii",
    2: "Sultanahmet Camii",
    3: "Galata Kulesi",
    // Backend'den gelen verilere göre genişletilebilir
  };

  const filteredData = useMemo(() => {
    return predictions.filter((item) => {
      const placeId = item.place_id ? item.place_id.toString() : '';
      const matchPlace = filterPlace ? placeId === filterPlace : true;
      const matchSearch = item.image_url.toLowerCase().includes(searchTerm.toLowerCase());
      return matchPlace && matchSearch;
    });
  }, [filterPlace, searchTerm, predictions]);

  const uniquePlaces = Array.from(new Set(predictions.map((d) => d.place_id)));
  
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const openModal = (imageUrl) => {
    setModalImage(imageUrl);
  };

  const closeModal = () => {
    setModalImage(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-gray-50">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
        <p className="ml-4 text-xl">Tahminler yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-red-400 p-6 text-center">
        <XCircle className="h-16 w-16 mb-4" />
        <p className="text-2xl font-semibold">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 bg-gray-900 text-gray-50 min-h-screen font-sans">
      <div className="flex items-center gap-4 mb-8">
        <Image className="h-12 w-12 text-blue-400" />
        <h1 className="text-4xl font-extrabold text-white">Tahminler</h1>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 mb-8 p-4 bg-gray-800 rounded-2xl border border-gray-700">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Görsel URL'si ile ara..."
            className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600 transition-colors"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="w-full sm:w-64 px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors cursor-pointer"
          value={filterPlace}
          onChange={(e) => {
            setFilterPlace(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">Tüm Mekanlar</option>
          {uniquePlaces.map((id) => (
            <option key={id} value={id}>
              {placeNameMap[id] || `Mekan ID: ${id}`}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-700">
              <tr className="text-left text-sm font-semibold text-gray-300">
                <th className="px-6 py-4">Kullanıcı</th>
                <th className="px-6 py-4">Tahmin Edilen Mekan</th>
                <th className="px-6 py-4">Oran</th>
                <th className="px-6 py-4">Yüklenme Tarihi</th>
                <th className="px-6 py-4">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-gray-500">
                    Kayıtlı tahmin bulunamadı.
                  </td>
                </tr>
              ) : (
                paginatedData.map((pred, index) => (
                  <tr key={pred.id} className="hover:bg-gray-700 transition-colors duration-200">
                    <td className="px-6 py-4 font-medium text-gray-300">{pred.user.username}</td>
                    <td className="px-6 py-4 text-gray-400">{placeNameMap[pred.location] || `${pred.location}`}</td>
                    <td className="px-6 py-4 text-gray-400">%{placeNameMap[pred.confidence] || `${((pred.confidence)* 100).toFixed(1)}`}</td>
                    <td className="px-6 py-4 text-gray-500">{new Date(pred.timestamp).toLocaleString("tr-TR")}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openModal(pred.image_url)}
                        className="inline-flex items-center gap-1 bg-blue-600/20 text-blue-400 px-3 py-1 rounded-lg hover:bg-blue-600/30 transition-colors duration-200 text-sm"
                        title="Görseli Görüntüle"
                      >
                        <Image className="h-4 w-4" /> Görüntüle
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Sayfalama --- */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <span className="text-sm text-gray-400">
            Toplam {filteredData.length} kayıt | Sayfa {currentPage} / {totalPages}
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg border border-gray-700 transition-colors ${
                currentPage === 1 ? "text-gray-600 cursor-not-allowed" : "hover:bg-gray-700 text-gray-300"
              }`}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg border border-gray-700 transition-colors ${
                currentPage === totalPages ? "text-gray-600 cursor-not-allowed" : "hover:bg-gray-700 text-gray-300"
              }`}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* --- Görsel Modal --- */}
      {modalImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-[9999]"
          onClick={closeModal}
        >
          <img
            src={`${API_URL}${modalImage}`}
            alt="Tahmin Edilen Görsel"
            className="max-w-full max-h-[90vh] rounded-xl shadow-2xl border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}